import { Skeleton, TableCell, TableRow } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import useWidth from '../../../components/Hooks/useWidth';
import { RenderItemProps } from '../../../components/ListV2/components/table/VirtualizedTable.types';
import TextCell from '../../../components/ListV2/components/table/cells/TextCell';
import { useTableScrollerContext } from '../../../components/ListV2/components/table/scroller/useTableScroller';
import { FAKE_COLUMN_SIZE } from '../../../components/ListV2/components/table/virtualizedTable.styles';
import LangEnum from '../../../lang/LangEnum';
import { useTranslation } from '../../translation/TranslationContext';
import { UseSummarizeColumnsProps, useSummarizeColumns } from '../hooks/useSummarizeColumns';

interface SummarizeRowProps<T> extends UseSummarizeColumnsProps<T> {
  className: RenderItemProps<T>['className'];
  isLoadingTotals: boolean;
  isUsingCheckbox?: boolean;
  fixedHorizontalScroll?: boolean;
  isScrolling?: boolean;
  /** @default true */
  hasActionsColumn?: boolean;
}

const parentClasses = makeStyles()((theme) => {
  return {
    fix: {
      position: 'sticky',
      left: 0,
      backgroundColor: 'white',
      boxShadow: '-6px 0 10px -2px white',
      zIndex: 1,
    },
    removePadding: {
      padding: 0,
    },
    summary: {
      paddingLeft: theme.spacing(2),
    },
    summarizeCell: {
      color: theme.palette.text.primary,
      textTransform: 'unset',
      fontSize: theme.typography.body1.fontSize,
      fontWeight: 600,
      backgroundColor: `${theme.palette.background.default} !important`,
      '& #text-cell-first-field': {
        fontSize: theme.typography.body1.fontSize,
        fontWeight: 600,
      },
      '& #text-cell-second-field': {
        fontSize: theme.typography.body1.fontSize,
        fontWeight: 600,
      },
    },
    fixedScrolledCell: {
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    fixedRight: {
      position: 'sticky',
      right: 0,
      width: FAKE_COLUMN_SIZE,
      minWidth: FAKE_COLUMN_SIZE,
      maxWidth: FAKE_COLUMN_SIZE,
      padding: 0,
      backgroundColor: theme.palette.background.default,
      boxShadow: `-6px 0 10px -2px ${theme.palette.background.default}`,
    },
    summarizeRow: {
      '& th': {
        height: 32,
        maxHeight: 32,
      },
      '& th:first-child': {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderBottomLeftRadius: theme.shape.borderRadius,
      },
      '& th:last-child': {
        borderTopRightRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
      },
    },
  };
});

const SummarizeRow = <T,>({
  className,
  visibleColumns,
  isLoadingTotals,
  fixedHorizontalScroll,
  isUsingCheckbox,
  hasActionsColumn = true,
}: SummarizeRowProps<T>) => {
  const { translate } = useTranslation();
  const { colspan, summarizeColumns, fillerColumnCount } = useSummarizeColumns({ visibleColumns });
  const { classes, cx } = parentClasses();
  const { mobileWidth } = useWidth();
  const { isScrolling } = useTableScrollerContext();

  if (isLoadingTotals) {
    return (
      <TableRow className={classes.summarizeRow}>
        <TableCell colSpan={99} className={cx(className, classes.summarizeCell)}>
          <Skeleton variant="rectangular" animation="wave" />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow className={classes.summarizeRow}>
      <TableCell
        className={cx({
          [className!]: true,
          [classes.summarizeCell]: true,
          [classes.fix]: fixedHorizontalScroll && !mobileWidth,
          [classes.fixedScrolledCell]: fixedHorizontalScroll && isScrolling && !mobileWidth,
          [classes.removePadding]: true,
        })}
        colSpan={isUsingCheckbox ? 2 : 1}
      >
        <TextCell className={classes.summary} firstField={translate(LangEnum.GLOBAL_SUMMARY)} />
      </TableCell>
      {colspan !== 1 && (
        <TableCell colSpan={colspan - 1} className={cx(className, classes.summarizeCell)} />
      )}
      {summarizeColumns.map((column) => (
        <TableCell key={column.title} className={cx(className, classes.summarizeCell)}>
          {column.renderTopColumn && column.renderTopColumn()}
        </TableCell>
      ))}
      {fillerColumnCount > 0 && (
        <TableCell
          colSpan={fillerColumnCount}
          className={cx({ [className!]: true, [classes.summarizeCell]: true })}
        />
      )}
      {hasActionsColumn && (
        <TableCell className={cx(className, classes.summarizeCell, classes.fixedRight)} />
      )}
    </TableRow>
  );
};

export default SummarizeRow;
